import React from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Paper,
    Fade,
    Card,
    CardContent,
    Chip,
    Alert,
    TextareaAutosize
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonIcon from '@mui/icons-material/Person';
import ComputerIcon from '@mui/icons-material/Computer';
import CheckIcon from '@mui/icons-material/Check';
import AIIcon from '../../../assets/svgs/AI-Icon.svg';

const AiTestPromptTab = ({
    selectedMessage,
    setSelectedMessage,
    testNotes,
    setTestNotes,
    testPromptTabLoading,
    testPromptTabResult,
    handleTestPromptTab,
    conversationHistory,
    question,
    aiSettings
}) => {
    const handleMessageSelect = (message) => {
        setSelectedMessage(message);
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '20px',
            height: '100%',
            overflow: 'auto',
            paddingTop: '8px'
        }}>
            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                marginBottom: '8px' 
            }}>
                <PlayArrowIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                    AI Test Prompt
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ 
                color: '#6c757d', 
                marginBottom: '16px',
                lineHeight: 1.6
            }}>
                Select an AI assistant message from the conversation and add notes to test how the AI will process it for this specific field.
            </Typography>

            {/* Conversation History */}
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px', color: '#2c3e50' }}>
                    Select a Message from AI Chat:
                </Typography>
                
                {conversationHistory.filter(message => message.role === 'assistant').length === 0 ? (
                    <Alert severity="info" sx={{ marginBottom: '16px' }}>
                        No AI assistant messages available. Please go to the AI Chat tab and have a conversation with the AI first.
                    </Alert>
                ) : (
                    <Box sx={{ 
                        maxHeight: '200px', 
                        overflow: 'auto', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '8px',
                        padding: '8px'
                    }}>
                        {conversationHistory.filter(message => message.role === 'assistant').map((message, index) => (
                            <Card
                                key={index}
                                sx={{
                                    marginBottom: '8px',
                                    cursor: 'pointer',
                                    border: selectedMessage === message ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                    backgroundColor: selectedMessage === message ? '#e3f2fd' : 'white',
                                    '&:hover': {
                                        backgroundColor: selectedMessage === message ? '#e3f2fd' : '#f5f5f5'
                                    },
                                    transition: 'all 0.2s ease',
                                    position: 'relative'
                                }}
                                onClick={() => handleMessageSelect(message)}
                            >
                                <CardContent sx={{ padding: '12px !important' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                        {message.role === 'user' ? (
                                            <PersonIcon sx={{ color: '#1976d2', fontSize: '20px', marginTop: '2px' }} />
                                        ) : (
                                            <ComputerIcon sx={{ color: '#4caf50', fontSize: '20px', marginTop: '2px' }} />
                                        )}
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                    {message.role === 'user' ? 'You' : 'AI Assistant'}
                                                </Typography>
                                                <Chip 
                                                    label={message.role === 'user' ? 'User' : 'AI'} 
                                                    size="small" 
                                                    color={message.role === 'user' ? 'primary' : 'success'}
                                                    sx={{ fontSize: '10px', height: '20px' }}
                                                />
                                                {selectedMessage === message && (
                                                    <Chip 
                                                        icon={<CheckIcon />}
                                                        label="Selected" 
                                                        size="small" 
                                                        color="primary"
                                                        sx={{ fontSize: '10px', height: '20px' }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="body2" sx={{ 
                                                color: '#2c3e50',
                                                lineHeight: 1.4,
                                                maxHeight: '60px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical'
                                            }}>
                                                {message.content}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Notes Input */}
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '8px', color: '#2c3e50' }}>
                    Add Notes:
                </Typography>
                <TextareaAutosize
                    minRows={2}
                    maxRows={6}
                    placeholder="Add additional context or notes for the test (e.g., 'This is for a patient with diabetes')"
                    value={testNotes}
                    onChange={(e) => setTestNotes(e.target.value)}
                    required
                    style={{
                        width: '100%',
                        minHeight: '40px',
                        padding: '12px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        fontFamily: 'inherit',
                        border: '2px solid #e0e0e0',
                        borderRadius: '6px',
                        resize: 'vertical',
                        outline: 'none',
                        backgroundColor: '#fafafa',
                        transition: 'all 0.2s ease',
                        '&:focus': {
                            borderColor: '#1976d2',
                            backgroundColor: 'white'
                        }
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = '#1976d2';
                        e.target.style.backgroundColor = 'white';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e0e0e0';
                        e.target.style.backgroundColor = '#fafafa';
                    }}
                />
            </Box>

            {/* Test Button */}
            <Box sx={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end',
                marginTop: '8px'
            }}>
                <Button
                    variant="contained"
                    onClick={handleTestPromptTab}
                    disabled={testPromptTabLoading || !selectedMessage || !testNotes.trim() || !aiSettings.aiDescription.trim()}
                    startIcon={testPromptTabLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: '160px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                        }
                    }}
                >
                    {testPromptTabLoading ? 'Testing...' : 'AI Test Prompt'}
                </Button>
            </Box>

            {/* Test Result Display */}
            {testPromptTabResult && (
                <Fade in={true}>
                    <Paper sx={{
                        padding: '20px',
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196f3',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
                        marginTop: '16px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <AIIcon sx={{ color: '#1976d2' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                Test Result
                            </Typography>
                        </Box>
                        
                        <Box sx={{ marginBottom: '16px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', marginBottom: '4px' }}>
                                Test Data:
                            </Typography>
                            <Typography variant="body2" sx={{ 
                                color: '#1565c0',
                                fontStyle: 'italic',
                                backgroundColor: '#f5f5f5',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0',
                                whiteSpace: 'pre-line'
                            }}>
                                "{testPromptTabResult.testData}"
                            </Typography>
                        </Box>

                        <Box sx={{ marginBottom: '16px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', marginBottom: '12px' }}>
                                AI Response:
                            </Typography>
                            {testPromptTabResult.aiResponse?.suggestions ? 
                                Object.entries(testPromptTabResult.aiResponse.suggestions).map(([fieldAccessor, suggestion]) => {
                                    // Handle suggestion object structure
                                    const isSuggestionObject = suggestion && typeof suggestion === 'object' && !Array.isArray(suggestion) && suggestion.value;
                                    
                                    return (
                                        <Box key={fieldAccessor} sx={{ 
                                            marginBottom: '12px',
                                            padding: '12px',
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            borderLeft: '4px solid #1976d2'
                                        }}>
                                            <Typography variant="subtitle2" sx={{ 
                                                fontWeight: 600, 
                                                color: '#2c3e50',
                                                marginBottom: '8px',
                                                fontSize: '13px'
                                            }}>
                                                {fieldAccessor}
                                            </Typography>
                                            
                                            {isSuggestionObject ? (
                                                <Box>
                                                    {/* Main Value */}
                                                    <Box sx={{ marginBottom: '8px' }}>
                                                        <Typography variant="body2" sx={{ 
                                                            fontWeight: 600, 
                                                            color: '#1976d2',
                                                            fontSize: '14px',
                                                            marginBottom: '2px'
                                                        }}>
                                                            Value:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ 
                                                            color: '#495057',
                                                            fontSize: '14px',
                                                            lineHeight: 1.5,
                                                            backgroundColor: 'white',
                                                            padding: '6px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #dee2e6'
                                                        }}>
                                                            {suggestion.value}
                                                        </Typography>
                                                    </Box>

                                                    {/* Confidence */}
                                                    {suggestion.confidence && (
                                                        <Box sx={{ marginBottom: '8px' }}>
                                                            <Typography variant="body2" sx={{ 
                                                                fontWeight: 600, 
                                                                color: '#6c757d',
                                                                fontSize: '12px',
                                                                marginBottom: '2px'
                                                            }}>
                                                                Confidence: {(suggestion.confidence * 100).toFixed(1)}%
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* Explanation */}
                                                    {suggestion.explanation && (
                                                        <Box sx={{ marginBottom: '8px' }}>
                                                            <Typography variant="body2" sx={{ 
                                                                fontWeight: 600, 
                                                                color: '#6c757d',
                                                                fontSize: '12px',
                                                                marginBottom: '2px'
                                                            }}>
                                                                Explanation:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ 
                                                                color: '#495057',
                                                                fontSize: '13px',
                                                                lineHeight: 1.4,
                                                                fontStyle: 'italic'
                                                            }}>
                                                                {suggestion.explanation}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* Field Label */}
                                                    {suggestion.fieldLabel && (
                                                        <Box sx={{ marginBottom: '8px' }}>
                                                            <Typography variant="body2" sx={{ 
                                                                fontWeight: 600, 
                                                                color: '#6c757d',
                                                                fontSize: '12px',
                                                                marginBottom: '2px'
                                                            }}>
                                                                Field:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ 
                                                                color: '#495057',
                                                                fontSize: '13px'
                                                            }}>
                                                                {suggestion.fieldLabel}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                </Box>
                                            ) : (
                                                /* Fallback for simple values */
                                                <Typography variant="body2" sx={{ 
                                                    color: '#495057',
                                                    fontSize: '14px',
                                                    lineHeight: 1.5,
                                                    whiteSpace: 'pre-wrap'
                                                }}>
                                                    {Array.isArray(suggestion) ? suggestion.join(', ') : String(suggestion)}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                }) : 
                                <Box sx={{ 
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                                        No suggestions generated
                                    </Typography>
                                </Box>
                            }
                        </Box>

                        {testPromptTabResult.aiResponse?.summary && (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', marginBottom: '4px' }}>
                                    Summary:
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    color: '#1565c0',
                                    fontStyle: 'italic'
                                }}>
                                    {testPromptTabResult.aiResponse.summary}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Fade>
            )}

        </Box>
    );
};

export default AiTestPromptTab;
